

import { useState } from "react";
import SearchableDropdown from "../components/SearchableDropdown";

interface Service {
  id: number;
  name: string;
  selling_price: number;
  base_price?: number;
  vendor_id?: number;
  vendor?: { id: number; name: string };
}

interface Vendor {
  id: number;
  name: string;
}

interface QuotationItem {
  id?: number;
  service_id?: number;
  description?: string;
  vendor_id?: number;
  quantity: number;
  unit_price: number;
  cost_price?: number;
  notes?: string;
}

interface BulkAddServicesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (services: QuotationItem[]) => void;
  services: Service[];
  vendors: Vendor[];
}

export default function BulkAddServicesModal({
  isOpen,
  onClose,
  onAdd,
  services,
  vendors,
}: BulkAddServicesModalProps) {
  const [selectedServices, setSelectedServices] = useState<number[]>([]);
  const [quantity, setQuantity] = useState("1");
  const [errors, setErrors] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");

  const handleServiceToggle = (serviceId: number) => {
    setSelectedServices(prev =>
      prev.includes(serviceId)
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleSelectAll = () => {
    if (selectedServices.length === services.length) {
      setSelectedServices([]);
    } else {
      setSelectedServices(services.map(s => s.id));
    }
  };

  const validate = (): boolean => {
    if (selectedServices.length === 0) {
      setErrors("Please select at least one service");
      return false;
    }
    if (parseInt(quantity) <= 0) {
      setErrors("Quantity must be greater than 0");
      return false;
    }
    return true;
  };

  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    service.vendor?.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSubmit = () => {
    if (!validate()) return;

    const newServices: QuotationItem[] = selectedServices.map(serviceId => {
      const service = services.find(s => s.id === serviceId);
      return {
        service_id: service?.id,
        description: service?.name || "",
        vendor_id: service?.vendor_id,
        quantity: parseInt(quantity),
        unit_price: service?.selling_price || 0,
        cost_price: service?.base_price || 0,
        notes: "",
      };
    });

    onAdd(newServices);
    setSelectedServices([]);
    setQuantity("1");
    setErrors("");
    setSearchQuery("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-stone-900/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-stone-200 bg-gradient-to-r from-amber-50 to-white flex-shrink-0">
          <h3 className="text-lg font-bold text-stone-800">Bulk Add Services</h3>
          <p className="text-xs text-stone-500 mt-1">Select multiple services to add at once</p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Search Input */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-2">Search Services</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by service name or vendor..."
              className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
            />
          </div>

          {/* Quantity Input */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-2">Default Quantity for All Selected Services</label>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
            />
          </div>

          {/* Select All */}
          <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <input
              type="checkbox"
              id="selectAll"
              checked={selectedServices.length === filteredServices.length && filteredServices.length > 0}
              onChange={handleSelectAll}
              className="w-4 h-4 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
            />
            <label htmlFor="selectAll" className="text-sm font-medium text-stone-700 cursor-pointer flex-1">
              {selectedServices.length === filteredServices.length && filteredServices.length > 0
                ? "Deselect All"
                : "Select All"}
            </label>
            <span className="text-xs text-stone-500">
              {selectedServices.length} of {filteredServices.length} selected
            </span>
          </div>

          {/* Services List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredServices.length === 0 ? (
              <p className="text-sm text-stone-400 text-center py-4">
                {searchQuery ? "No services match your search" : "No services available"}
              </p>
            ) : (
              filteredServices.map(service => (
                <div
                  key={service.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-stone-200 hover:border-amber-300 hover:bg-amber-50/50 transition-colors cursor-pointer"
                  onClick={() => handleServiceToggle(service.id)}
                >
                  <input
                    type="checkbox"
                    checked={selectedServices.includes(service.id)}
                    onChange={() => handleServiceToggle(service.id)}
                    className="w-4 h-4 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-700 truncate">{service.name}</p>
                    <div className="flex gap-3 text-xs text-stone-500 mt-0.5">
                      <span>₹{service.selling_price.toLocaleString('en-IN')}</span>
                      {service.vendor && <span>{service.vendor.name}</span>}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {errors && (
            <div className="p-3 bg-rose-50 text-rose-700 rounded-lg text-sm border border-rose-200">
              {errors}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-stone-200 bg-stone-50 px-6 py-3 flex gap-2 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg bg-white text-stone-600 font-semibold text-sm border border-stone-200 hover:bg-stone-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 rounded-lg bg-amber-600 text-white font-semibold text-sm hover:bg-amber-700 transition-colors"
          >
            Add {selectedServices.length} Service{selectedServices.length !== 1 ? "s" : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
